/**
 * CartDrawer — Panier coulissant depuis la droite.
 * Affiche les items groupés par carte, quantités +/-, bouton "Voir mon panier".
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../contexts/CartContext";

const eur = (c) => (c / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

export default function CartDrawer() {
  const nav = useNavigate();
  const { drawerOpen, setDrawerOpen, itemsByCard, grossTotalCents,
          totalItems, updateQty, removeItem } = useCart();

  if (!drawerOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-ardoise/50 z-[60]"
        onClick={() => setDrawerOpen(false)}
        data-testid="cart-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-lin z-[70] border-l border-pierre shadow-2xl flex flex-col"
        data-testid="cart-drawer"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-pierre">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ardoise/60">Votre panier</div>
            <h2 className="font-serif text-xl text-domaine mt-0.5">
              {totalItems} article{totalItems > 1 ? "s" : ""}
            </h2>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-ardoise hover:text-domaine p-1"
            aria-label="Fermer le panier"
            data-testid="cart-drawer-close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {itemsByCard.length === 0 && (
            <div className="text-center py-16">
              <ShoppingBag size={40} className="text-ardoise/30 mx-auto mb-3" />
              <p className="text-sm text-ardoise/60">Votre panier est vide.</p>
              <p className="text-xs text-ardoise/40 mt-1">Découvrez nos gourmandises et coffrets.</p>
            </div>
          )}

          {itemsByCard.map((g) => (
            <div key={g.card_id} className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-domaine border-b border-pierre pb-2">
                Carte : {g.card_name}
              </div>
              {g.items.map((it) => (
                <div
                  key={`${it.card_id}:${it.product_id}`}
                  className="flex items-start gap-3 pb-3 border-b border-pierre/40"
                  data-testid={`cart-item-${it.product_id}`}
                >
                  {it.photo_url && (
                    <img src={it.photo_url} alt={it.name}
                         className="w-14 h-14 object-cover border border-pierre flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ardoise line-clamp-2">{it.name}</div>
                    <div className="text-xs text-domaine mt-0.5">{eur(it.unit_price_cents)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQty(it.product_id, it.card_id, it.qty - 1)}
                        className="w-6 h-6 border border-pierre inline-flex items-center justify-center text-ardoise hover:bg-pierre/40"
                        aria-label="Diminuer"
                        data-testid={`cart-dec-${it.product_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-sm">{it.qty}</span>
                      <button
                        onClick={() => updateQty(it.product_id, it.card_id, it.qty + 1)}
                        className="w-6 h-6 border border-pierre inline-flex items-center justify-center text-ardoise hover:bg-pierre/40"
                        aria-label="Augmenter"
                        data-testid={`cart-inc-${it.product_id}`}
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeItem(it.product_id, it.card_id)}
                        className="ml-auto text-ardoise/40 hover:text-red-600"
                        aria-label="Retirer"
                        data-testid={`cart-remove-${it.product_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-domaine font-medium whitespace-nowrap">
                    {eur(it.unit_price_cents * it.qty)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {itemsByCard.length > 0 && (
          <footer className="p-5 border-t border-pierre space-y-3 bg-white">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-ardoise/60">
                Sous-total (TTC)
              </span>
              <span className="font-serif text-2xl text-domaine">{eur(grossTotalCents)}</span>
            </div>
            <button
              onClick={() => { setDrawerOpen(false); nav("/panier"); }}
              className="btn-domaine w-full !py-3 inline-flex items-center justify-center gap-2"
              data-testid="cart-drawer-view-cart"
            >
              Voir mon panier
            </button>
            <p className="text-[10px] text-ardoise/50 text-center">
              Retrait sur place au Domaine · Paiement sécurisé Stripe
            </p>
          </footer>
        )}
      </aside>
    </>
  );
}
